import { useState, useEffect } from "react";
import { getSeoCardsDataApiV1SeoCardsGet } from "@/backend-sdk/client/sdk.gen";
import type { SeoCardsData } from "@/backend-sdk/client/types.gen";

interface UseSEOCardsReturn {
  data: SeoCardsData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Os cards, as séries e as duas tabelas da página SEO.
 *
 * <auditoria-18> O hook **não manda mais `brand_terms`**. O filtro de marca é resolvido no
 * servidor a partir da lista persistida da loja (`GET /api/v1/seo/brand-terms`), e a
 * resposta diz em `termos_de_marca` quais termos filtraram os dois cards de marca e a
 * `palavras_chaves_marca_table`. Enquanto a tela ecoava os termos, ela e o servidor podiam
 * discordar — e discordavam: numa loja sem termo configurado, a tela deduzia do nome da
 * conta e o servidor do domínio da propriedade, com números diferentes no mesmo card.
 *
 * `brandTermsNonce` existe porque a lista agora é gravada por outra rota: salvar um termo
 * não muda parâmetro nenhum desta requisição, e sem o nonce a página continuaria mostrando
 * o recorte antigo até alguém trocar de data.
 */
export const useSEOCards = (
  startDate: string,
  endDate: string,
  comparison: string,
  customerUuid?: string,
  accountId?: string,
  campaignFiltersNonce?: number,
  brandTermsNonce?: number,
): UseSEOCardsReturn => {
  const [data, setData] = useState<SeoCardsData | null>(null);
  // Começa carregando: a busca só sai no efeito, e o primeiro desenho acontece antes dele —
  // com `false`, a tela mostrava o bloco como indisponível antes de a requisição sair.
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate || !customerUuid) {
        // Sem loja não há o que esperar; sem datas, o filtro ainda está montando.
        if (!customerUuid) setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: response } = await getSeoCardsDataApiV1SeoCardsGet({
          query: {
            start_date: startDate,
            end_date: endDate,
            comparison,
            customer_uuid: customerUuid,
            account_id: accountId,
          },
          throwOnError: true,
        });
        setData(response.data || null);
      } catch (err) {
        console.error("🔍 useSEOCards - Erro ao buscar dados:", err);
        setError("Erro ao carregar dados dos cards SEO");
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    campaignFiltersNonce,
    brandTermsNonce,
  ]);

  return {
    data,
    loading,
    error,
  };
};
